import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { sentry } from '../../../../../sentry';
import { LocationSectorApi } from '../../../../core/api/location-sectors/location-sectors.api';
import { CreateLocationSectorInput, UpdateLocationSectorInput } from '../../../../core/api/location-sectors/location-sectors.type';
import { AuthenticationService } from '../../../../core/authentication/authentication.service';
import { CustomMessagesService } from '../../../../core/errors/custom-messages.service';
import { LocationSector } from '../../../../core/interfaces/locations-sectors';
import { locationSectorNameUniqueAsyncValidator } from './dialog-location-sector.validator';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

export interface DialogLocationSectorData {
  locationSector: LocationSector;
}

export interface DialogLocationSectorResult {
  reload?: boolean;
  locationSector?: LocationSector;
}

@Component({
  templateUrl: './dialog-location-sector.component.html',
  styleUrls: ['./dialog-location-sector.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DialogLocationSectorComponent implements OnInit {

  public isUpdate = false;
  public isLoading = false;
  public locationSectorForm: FormGroup;
  public isViewMode = !this.authenticationService.hasPermission('locations-edit');

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: DialogLocationSectorData,
    private readonly formBuilder: FormBuilder,
    private readonly translationLoader: FuseTranslationLoaderService,
    private readonly locationSectorApi: LocationSectorApi,
    private readonly messageService: CustomMessagesService,
    private readonly matDialogRef: MatDialogRef<DialogLocationSectorComponent>,
    private readonly authenticationService: AuthenticationService
  ) {
    this.translationLoader.loadJsonTranslations({ english, portuguese, spanish });
  }

  ngOnInit(): void {

    this.locationSectorForm = this.formBuilder.group({
      name: new FormControl(this.data.locationSector.name, {
        validators: [Validators.required, Validators.maxLength(100)],
        asyncValidators: [
          locationSectorNameUniqueAsyncValidator({
            sectorId: this.data.locationSector.id,
            locationId: this.data.locationSector.location.id,
            locationSectorApi: this.locationSectorApi
          })
        ]
      }),
      location: new FormControl(this.data.locationSector.location, [Validators.required]),
      description: [this.data.locationSector.description, [Validators.maxLength(2000)]]
    });

    this.locationSectorForm.get('location').disable();

    if (this.isViewMode) {
      this.locationSectorForm.disable();
    }
  }

  async saveLocationSector(): Promise<void> {
    try {
      this.isLoading = true;

      const result = this.data.locationSector.id
        ? await this.locationSectorApi.update(this.prepareUpdateInput())
        : await this.locationSectorApi.create(this.prepareCreateInput());

      this.messageService.success('LOCATION_SECTOR_DIALOG.MESSAGE.SUCCESS');

      this.matDialogRef.close({
        reload: true,
        locationSector: result
      });
    } catch (error) {
      console.error(error);
      sentry.captureException(error);
      this.messageService.error(error);
    } finally {
      this.isLoading = false;
    }

  }

  private prepareCreateInput(): CreateLocationSectorInput {
    const form = this.locationSectorForm.getRawValue();
    return {
      name: form.name,
      description: form.description,
      locationId: this.data.locationSector.location.id
    };
  }

  private prepareUpdateInput(): UpdateLocationSectorInput {
    const form = this.locationSectorForm.getRawValue();
    return {
      id: this.data.locationSector.id,
      name: form.name,
      description: form.description,
    };
  }

}
